import { useState, useRef, useEffect, useCallback } from 'react';
import { cx } from '@emotion/css';
import styles from '../navigation.module.scss';
import { motion } from 'framer-motion';
import { stihlMenuAnimate, stihlMenuItemAnimate } from '../helpers/animations';
import { GlobalLink } from '../../../domain/static-content/static-content.static-generation.model';
import { useTranslation } from 'next-i18next';
import { useStihlIdNavigation } from '../hooks/useStihlIdNavigation';
import { useNavigation } from '../context/navigation-context-provider';
import { getLocale } from '../../../util/locales/locales';
import { Spinner } from '@phoenix/design-system/frontend/ui/spinner/spinner';
import { getDesignSystemIconWithFallback, getInitials } from '../helpers/utils';
import { useOutsideClick } from '../hooks/useOutsideClick';
import Skeleton from 'react-loading-skeleton';
import { StihlIdMenuItem } from './stihl-id-menu-item';

export const StihlIdMenuSection = () => {
  const { t } = useTranslation();
  const { auth, config } = useNavigation();
  const { stihlIdNavigationItemsQuery } = useStihlIdNavigation(auth?.isAuthenticated, config?.locale);
  const [isOpen, setIsOpen] = useState(false);
  const outsideClick = useOutsideClick(() => setIsOpen(false));
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  const firstMenuItemSetRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  firstMenuItemSetRef.current = false;

  // --- Add callback params logic ---
  const callbackAppName = config?.callbackAppName;
  const callbackUrl = config?.callbackUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const addCallbackParams = useCallback(
    (url: string) => {
      if (!callbackAppName) {
        return url;
      }
      if (!url.includes('/account/') && !url.includes('/profile/')) {
        return url;
      }
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('callbackUrl', callbackUrl);
        u.searchParams.set('callbackAppName', callbackAppName);
        return u.toString().replace(window.location.origin, '');
      } catch {
        return url;
      }
    },
    [callbackAppName, callbackUrl]
  );

  // Recursively transform nav items
  const transformNavItems = useCallback(
    (items: any[]): any[] => {
      return items?.map((item) => {
        const newItem = { ...item };
        if (typeof newItem.url === 'string') {
          newItem.url = addCallbackParams(newItem.url);
        }
        if (Array.isArray(newItem.children)) {
          newItem.children = transformNavItems(newItem.children);
        }
        return newItem;
      });
    },
    [addCallbackParams]
  );

  const navItemsWithCallback =
    callbackAppName && stihlIdNavigationItemsQuery.data
      ? transformNavItems(stihlIdNavigationItemsQuery.data)
      : stihlIdNavigationItemsQuery.data;
  // --- End callback params logic ---

  // Focus first menu item when menu opens
  useEffect(() => {
    if (isOpen && stihlIdNavigationItemsQuery.isSuccess && firstMenuItemRef.current) {
      setTimeout(() => {
        firstMenuItemRef.current?.focus();
      }, 0);
    }
  }, [isOpen, stihlIdNavigationItemsQuery.isSuccess]);

  // Handle Escape key to close menu
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  // Restore focus to trigger button when menu closes
  useEffect(() => {
    if (!isOpen && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isOpen]);

  return !auth?.isAuthenticated ? (
    <a
      href={auth?.postLoginRedirectUrl?.replace('{locale}', getLocale().RFC5646?.toLowerCase())}
      className={cx('ml-4 mr-8 stihl-text-body-xs-reg', styles.metaNavItem)}
      target="_self"
      data-testid="navigation_stihlid_menu_login"
    >
      <svg className="block h-4 w-4 fill-current text-primary-black">
        <use xlinkHref={getDesignSystemIconWithFallback('icon-user.svg') + '#img'} />
      </svg>
      <span>{t('shared:topBar_meta_login_label')}</span>
    </a>
  ) : (
    <div data-testid="navigation_stihlid_menu" ref={outsideClick}>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          className="relative ml-4 mr-8 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-secondary-deep text-primary-white focus-visible:outline-offset-[3px] focus-visible:outline-[#252525] focus-visible:outline-solid"
          aria-label={!auth.displayName ? t('shared:topBar_meta_userMenu_load_stihl_id_user') : undefined}
          aria-expanded={isOpen}
          aria-controls="stihlid-menu"
          aria-haspopup="true"
          id="stihlid-menu-button"
        >
          {auth.displayName ? (
            <div className="stihl-text-interactive-text" data-testid="navigation_stihlid_userInitials">
              {getInitials(auth.displayName)}
            </div>
          ) : (
            <Spinner
              className="h-4 w-4 rounded-half border-[0.15rem] border-gray-deep border-l-primary-white bg-transparent text-[0.625rem] after:w-4"
              data-testid="navigation_stihlid_userInitials_loading"
            />
          )}
        </button>
        <motion.div
          initial="exit"
          variants={stihlMenuAnimate}
          animate={isOpen ? 'enter' : 'exit'}
          className="absolute right-0 top-9 z-10 w-52 bg-transparent stihl-text-body-default-bold"
          id="stihlid-menu"
          role="menu"
          aria-labelledby="stihlid-menu-button"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <div className="mt-2 flex flex-col bg-primary-white text-left text-primary-black shadow-md">
            {auth.displayName && (
              <motion.div
                variants={stihlMenuItemAnimate}
                className="border-b border-secondary p-4 pb-2 text-primary-black"
                data-testid="username"
              >
                {auth.displayName}
              </motion.div>
            )}
            {stihlIdNavigationItemsQuery.isLoading && (
              <div className="flex items-center space-x-4">
                <Skeleton className="h-4 w-[250px]" />
              </div>
            )}
            {navItemsWithCallback && (
              <nav className="stihl-text-body-default-reg" aria-label={t('shared:shared_globalNav_stihlid_aria_label')}>
                <ul className="flex flex-col">
                  {navItemsWithCallback?.map((item: GlobalLink) => {
                    let ref;
                    if (!firstMenuItemSetRef.current) {
                      ref = firstMenuItemRef;
                      firstMenuItemSetRef.current = true;
                    }
                    return (
                      <StihlIdMenuItem item={item} key={`${item.id}-id-item`} onClick={() => setIsOpen(false)} ref={ref} role="menuitem" />
                    );
                  })}
                </ul>
              </nav>
            )}

            <hr className="border-secondary mx-4" />

            <motion.div variants={stihlMenuItemAnimate}>
              <button
                type="button"
                className={cx(
                  'p-4 flex w-full items-center gap-2 text-left text-primary-black stihl-text-body-default-bold',
                  'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline',
                  styles.stihlIdMenuItem
                )}
                data-testid="navigation_stihlid_menu_logout"
                onClick={() => {
                  setIsOpen(false);
                  auth?.onLogout?.();
                }}
              >
                <svg className="h-4 w-4 fill-current">
                  <use xlinkHref={getDesignSystemIconWithFallback('icon-exit-16.svg') + '#img'} />
                </svg>
                <span>{t('shared:topBar_meta_userMenu_logout')}</span>
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
